import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Book, ArrowLeft, Loader, GraduationCap, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import AuthContext from '../../contexts/AuthContext';

/**
 * Department Details Page component
 * Shows department information and all courses in the department
 */
const DepartmentDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [department, setDepartment] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Fetch department and courses
    const fetchDepartmentDetails = async () => {
      try {
        setLoading(true);
        
        // Fetch department details
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/departments/${id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch department details');
        }

        const data = await response.json();
        setDepartment(data.department);
        setCourses(data.courses);
      } catch (error) {
        console.error('Error fetching department details:', error);
        setError(error.message || 'An error occurred while fetching department data');
        toast.error('Failed to load department details');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchDepartmentDetails();
    }
  }, [id]);

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <Loader className="h-12 w-12 text-red-500 animate-spin mb-4" />
        <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300">
          Loading department details...
        </h2>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Error Loading Department
        </h2>
        <p className="text-gray-600 dark:text-gray-400 text-center max-w-md">
          {error}
        </p>
        <button
          onClick={() => navigate(-1)}
          className="mt-6 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        >
          <ArrowLeft className="mr-2 -ml-1 h-5 w-5" />
          Go Back
        </button>
      </div>
    );
  }

  // If department data is missing
  if (!department) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <AlertCircle className="h-12 w-12 text-yellow-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Department Not Found
        </h2>
        <p className="text-gray-600 dark:text-gray-400 text-center max-w-md">
          The department you're looking for couldn't be found or you don't have access to it.
        </p>
        <Link
          to="/departments"
          className="mt-6 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        >
          <ArrowLeft className="mr-2 -ml-1 h-5 w-5" />
          All Departments
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Navigation breadcrumbs */}
      <div className="flex items-center mb-6 text-sm">
        <Link to="/school" className="text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400">
          Home
        </Link>
        <span className="mx-2 text-gray-400 dark:text-gray-600">/</span>
        <Link to="/departments" className="text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400">
          Departments
        </Link>
        <span className="mx-2 text-gray-400 dark:text-gray-600">/</span>
        <span className="text-gray-700 dark:text-gray-300 font-medium">
          {department.name}
        </span>
      </div>

      {/* Department header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center">
              <GraduationCap className="h-8 w-8 text-red-500 mr-3" />
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                {department.name}
              </h1>
            </div>
            <p className="mt-2 text-lg text-gray-600 dark:text-gray-400">
              Department Code: <span className="font-medium">{department.code}</span>
            </p>
            {department.description && (
              <p className="mt-4 text-gray-700 dark:text-gray-300">
                {department.description}
              </p>
            )}
          </div>
          
          {/* Admin action buttons if user is admin */}
          {user && (user.role === 'admin' || user.role === 'moderator') && (
            <div className="flex space-x-2">
              <button
                onClick={() => navigate(`/admin/departments/${id}/edit`)}
                className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
              >
                Edit Department
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Courses list */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Courses
          </h2>
          
          {user && (user.role === 'admin' || user.role === 'moderator') && (
            <Link
              to="/admin/courses/new"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <Book className="mr-2 -ml-1 h-5 w-5" />
              Add Course
            </Link>
          )}
        </div>

        {courses.length === 0 ? (
          <div className="text-center py-8">
            <Book className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No Courses Yet
            </h3>
            <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
              This department doesn't have any courses listed yet.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course) => (
              <Link 
                key={course.id} 
                to={`/courses/${course.id}`}
                className="block bg-gray-50 dark:bg-gray-700 rounded-lg shadow hover:shadow-md transition-shadow p-5 border border-gray-200 dark:border-gray-600"
              >
                <div className="flex items-start">
                  <Book className="h-6 w-6 text-red-500 mr-3 mt-1" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {course.code}: {course.name}
                    </h3>
                    {course.description && (
                      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                        {course.description}
                      </p>
                    )}
                    <div className="mt-3 flex items-center text-xs text-gray-500 dark:text-gray-400">
                      <span className="bg-gray-200 dark:bg-gray-600 px-2 py-1 rounded">
                        {course.sectionCount || 0} sections
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DepartmentDetailsPage;